import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './UserManagement.css';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [walletEditMode, setWalletEditMode] = useState({});
  const [walletUpdates, setWalletUpdates] = useState({});
  const [kycDetails, setKycDetails] = useState(null);
  const [showKycModal, setShowKycModal] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', role: 'user' });
  const [newPassword, setNewPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://trcsite.net/api/admin/users?page=${currentPage}&limit=20&search=${searchTerm}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUsers(response.data.users);
      setTotalPages(response.data.totalPages);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    setDetailsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://trcsite.net/api/admin/users/${userId}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUserDetails(response.data);
      setSelectedUser(userId);
      
      // Initialize wallet edit state
      const initialWalletState = {};
      response.data.wallets.forEach(wallet => {
        initialWalletState[wallet.currency] = false;
      });
      setWalletEditMode(initialWalletState);
      setWalletUpdates({});
    } catch (error) {
      console.error('Error fetching user details:', error);
      alert('Failed to fetch user details');
    } finally {
      setDetailsLoading(false);
    }
  };

const fetchKycDetails = async (userId) => {
  try {
    console.log('🔄 Fetching KYC details for user ID:', userId);
    
    const token = localStorage.getItem('token');
    const response = await axios.get(`https://trcsite.net/api/admin/users/${userId}/kyc-details`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    console.log('✅ KYC Details Response:', response.data);
    
    if (response.data.success) {
      setKycDetails(response.data.kyc_application);
      setReviewNotes(response.data.kyc_application.review_notes || '');
      setShowKycModal(true);
    } else {
      throw new Error(response.data.error || 'Failed to fetch KYC details');
    }
  } catch (error) {
    console.error('❌ KYC Details Error:', error);
    
    let errorMessage = 'Failed to fetch KYC details';
    
    if (error.response) {
      console.log('Error response data:', error.response.data);
      
      errorMessage = error.response.data?.error || errorMessage;
      
      // Show user info if available
      if (error.response.data?.user_info) {
        errorMessage += `\nUser: ${error.response.data.user_info.name} (${error.response.data.user_info.email})`;
      }
      
      if (error.response.data?.details) {
        errorMessage += `\nDetails: ${error.response.data.details}`;
      }
    }
    
    alert(errorMessage);
  }
};

// Also add a button to check all KYC data
const checkAllKYCData = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get('https://trcsite.net/api/admin/debug/kyc-all-data', {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    console.log('📊 All KYC Data:', response.data);
    alert(`Total users with KYC: ${response.data.total_users}\nCheck console for details.`);
  } catch (error) {
    console.error('Error fetching all KYC data:', error);
    alert('Failed to fetch KYC data');
  }
};

  const updateUserRole = async (userId, newRole) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`https://trcsite.net/api/admin/users/${userId}/role`, 
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchUsers();
      alert('User role updated successfully');
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('Failed to update user role');
    }
  };

  const updateKYCStatus = async (userId, newStatus, notes = '') => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`https://trcsite.net/api/admin/users/${userId}/kyc`, 
        { 
          kyc_status: newStatus,
          review_notes: notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchUsers();
      if (selectedUser === userId) {
        fetchUserDetails(userId);
      }
      if (showKycModal) {
        setShowKycModal(false);
      }
      alert('KYC status updated successfully');
    } catch (error) {
      console.error('Error updating KYC status:', error);
      alert('Failed to update KYC status');
    }
  };

  const reviewKYCApplication = async (kycId, status, notes = '') => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`https://trcsite.net/api/admin/kyc/${kycId}/review`, 
        { 
          status: status,
          review_notes: notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh data
      fetchUsers();
      if (selectedUser) {
        fetchUserDetails(selectedUser);
      }
      setShowKycModal(false);
      alert(`KYC application ${status} successfully`);
    } catch (error) {
      console.error('Error reviewing KYC application:', error);
      alert('Failed to review KYC application');
    }
  };

  const updateWalletBalance = async (userId, currency, updates) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`https://trcsite.net/api/admin/users/${userId}/wallet`, 
        { 
          currency,
          ...updates
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh user details
      fetchUserDetails(userId);
      setWalletEditMode(prev => ({ ...prev, [currency]: false }));
      setWalletUpdates(prev => {
        const newUpdates = { ...prev };
        delete newUpdates[currency];
        return newUpdates;
      });
      
      alert('Wallet balance updated successfully');
    } catch (error) {
      console.error('Error updating wallet balance:', error);
      alert('Failed to update wallet balance');
    }
  };

  const handleWalletEdit = (currency) => {
    setWalletEditMode(prev => ({ ...prev, [currency]: true }));
    // Initialize updates with current values
    const currentWallet = userDetails.wallets.find(w => w.currency === currency);
    setWalletUpdates(prev => ({
      ...prev,
      [currency]: {
        balance: currentWallet.balance,
        locked: currentWallet.locked || 0
      }
    }));
  };

  const handleWalletChange = (currency, field, value) => {
    setWalletUpdates(prev => ({
      ...prev,
      [currency]: {
        ...prev[currency],
        [field]: parseFloat(value) || 0
      }
    }));
  };

  const cancelWalletEdit = (currency) => {
    setWalletEditMode(prev => ({ ...prev, [currency]: false }));
    setWalletUpdates(prev => {
      const newUpdates = { ...prev };
      delete newUpdates[currency];
      return newUpdates;
    });
  };

  const closeUserDetails = () => {
    setSelectedUser(null);
    setUserDetails(null);
    setWalletEditMode({});
    setWalletUpdates({});
  };

  const closeKycModal = () => {
    setShowKycModal(false);
    setKycDetails(null);
    setReviewNotes('');
  };

  const createUser = async (e) => {
    e.preventDefault();
    setSavingPassword(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('https://trcsite.net/api/admin/users', createForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCreateModal(false);
      setCreateForm({ name: '', email: '', password: '', role: 'user' });
      fetchUsers();
      alert('User created successfully');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to create user');
    } finally {
      setSavingPassword(false);
    }
  };

  const updateUserPassword = async (e) => {
    e.preventDefault();
    if (!passwordUser) return;
    setSavingPassword(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`https://trcsite.net/api/admin/users/${passwordUser.id}/password`,
        { password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPasswordUser(null);
      setNewPassword('');
      alert('Password updated. All sessions for this user were signed out.');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update password');
    } finally {
      setSavingPassword(false);
    }
  };

  const deleteUser = async (userToDelete) => {
    if (!window.confirm(`Delete user ${userToDelete.email}? This cannot be undone.`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`https://trcsite.net/api/admin/users/${userToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (selectedUser === userToDelete.id) {
        closeUserDetails();
      }
      fetchUsers();
      alert('User deleted');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete user');
    }
  };

  // KYC Image Display Component
  const KYCImage = ({ src, alt, title }) => (
    <div style={{ marginBottom: '20px' }}>
      <h4 style={{ color: '#b0b8c4', marginBottom: '10px' }}>{title}</h4>
      {src ? (
        <div>
          <img 
            src={src} 
            alt={alt}
            style={{
              maxWidth: '100%',
              maxHeight: '300px',
              border: '1px solid #344258',
              borderRadius: '4px',
              marginBottom: '5px'
            }}
          />
          <div style={{ fontSize: '12px', color: '#b0b8c4' }}>
            <a href={src} target="_blank" rel="noopener noreferrer" style={{ color: '#2962ff' }}>
              View Full Image
            </a>
          </div>
        </div>
      ) : (
        <div style={{ color: '#ff1744', fontStyle: 'italic' }}>Not provided</div>
      )}
    </div>
  );

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="management-section user-management">
      <div className="section-header user-management-header">
        <h2>User Management</h2>
        <div className="user-management-toolbar">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search users by UID, email, or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="um-btn um-btn-primary" onClick={() => setShowCreateModal(true)}>
            Create User
          </button>
        </div>
      </div>

      <div className="table-container user-table-wrap">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #344258' }}>
              <th style={{ padding: '12px', textAlign: 'left', color: '#b0b8c4' }}>UID</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#b0b8c4' }}>User</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#b0b8c4' }}>Email</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#b0b8c4' }}>KYC Status</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#b0b8c4' }}>Verified</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#b0b8c4' }}>Role</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#b0b8c4' }}>Joined</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#b0b8c4' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} style={{ borderBottom: '1px solid #2a3b52' }}>
                <td style={{ padding: '12px', fontFamily: 'monospace', fontWeight: 'bold', color: '#2962ff' }}>
                  {user.uid}
                </td>
                <td style={{ padding: '12px' }}>{user.name}</td>
                <td style={{ padding: '12px' }}>{user.email}</td>
                <td style={{ padding: '12px' }}>
                  <span style={{
                    color: user.kyc_status === 'verified' ? '#00c853' : 
                           user.kyc_status === 'pending' ? '#ffa726' : '#ff1744',
                    fontWeight: 'bold'
                  }}>
                    {user.kyc_status?.toUpperCase() || 'NOT SUBMITTED'}
                  </span>
                </td>
                <td style={{ padding: '12px' }}>
                  {user.is_verified ? (
                    <span style={{ color: '#00c853' }}>✓ Verified</span>
                  ) : (
                    <span style={{ color: '#ff1744' }}>Not Verified</span>
                  )}
                </td>
                <td style={{ padding: '12px' }}>
                  <select
                    value={user.role}
                    onChange={(e) => updateUserRole(user.id, e.target.value)}
                    style={{
                      padding: '6px 10px',
                      border: '1px solid #344258',
                      borderRadius: '4px',
                      backgroundColor: '#1a2332',
                      color: '#ffffff'
                    }}
                  >
                    <option value="user">User</option>
                    <option value="moderator">Moderator</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td style={{ padding: '12px' }}>
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
                <td style={{ padding: '12px' }}>
                  <button
                    onClick={() => fetchUserDetails(user.id)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#2962ff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      marginRight: '8px',
                      marginBottom: '5px'
                    }}
                  >
                    View Details
                  </button>

                  {user.kyc_status && user.kyc_status !== 'not_submitted' && (
                    <button
                      onClick={() => fetchKycDetails(user.id)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#ffa726',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        marginRight: '8px',
                        marginBottom: '5px'
                      }}
                    >
                      View KYC
                    </button>
                  )}

                  <select
                    value={user.kyc_status || 'not_submitted'}
                    onChange={(e) => updateKYCStatus(user.id, e.target.value)}
                    style={{
                      padding: '6px 10px',
                      border: '1px solid #344258',
                      borderRadius: '4px',
                      backgroundColor: '#1a2332',
                      color: '#ffffff',
                      fontSize: '12px'
                    }}
                  >
                    <option value="not_submitted">Not Submitted</option>
                    <option value="pending">Pending</option>
                    <option value="verified">Verified</option>
                    <option value="rejected">Rejected</option>
                  </select>
                  <button
                    onClick={() => { setPasswordUser(user); setNewPassword(''); }}
                    className="um-btn um-btn-small"
                  >
                    Password
                  </button>
                  <button
                    onClick={() => deleteUser(user)}
                    className="um-btn um-btn-danger um-btn-small"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* User Details Modal */}
      {selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1a2332',
            border: '1px solid #344258',
            borderRadius: '8px',
            padding: '20px',
            width: '90%',
            maxWidth: '1000px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            {detailsLoading ? (
              <div className="loading">Loading user details...</div>
            ) : userDetails ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h2>User Details: {userDetails.user.name} ({userDetails.user.uid})</h2>
                  <button
                    onClick={closeUserDetails}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#ff1744',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Close
                  </button>
                </div>

                {/* User Information */}
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ color: '#b0b8c4', marginBottom: '15px' }}>Basic Information</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                    <div>
                      <strong style={{ color: '#b0b8c4' }}>UID:</strong>
                      <div style={{ color: '#2962ff', fontFamily: 'monospace', fontWeight: 'bold' }}>{userDetails.user.uid}</div>
                    </div>
                    <div>
                      <strong style={{ color: '#b0b8c4' }}>Name:</strong>
                      <div style={{ color: '#ffffff' }}>{userDetails.user.name}</div>
                    </div>
                    <div>
                      <strong style={{ color: '#b0b8c4' }}>Email:</strong>
                      <div style={{ color: '#ffffff' }}>{userDetails.user.email}</div>
                    </div>
                    <div>
                      <strong style={{ color: '#b0b8c4' }}>Role:</strong>
                      <div style={{ color: '#ffffff' }}>{userDetails.user.role}</div>
                    </div>
                    <div>
                      <strong style={{ color: '#b0b8c4' }}>KYC Status:</strong>
                      <div style={{
                        color: userDetails.user.kyc_status === 'verified' ? '#00c853' : 
                               userDetails.user.kyc_status === 'pending' ? '#ffa726' : '#ff1744',
                        fontWeight: 'bold'
                      }}>
                        {userDetails.user.kyc_status?.toUpperCase() || 'NOT SUBMITTED'}
                      </div>
                    </div>
                    <div>
                      <strong style={{ color: '#b0b8c4' }}>Email Verified:</strong>
                      <div style={{ color: userDetails.user.is_verified ? '#00c853' : '#ff1744' }}>
                        {userDetails.user.is_verified ? 'Yes' : 'No'}
                      </div>
                    </div>
                    <div>
                      <strong style={{ color: '#b0b8c4' }}>Joined:</strong>
                      <div style={{ color: '#ffffff' }}>
                        {new Date(userDetails.user.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <strong style={{ color: '#b0b8c4' }}>Password:</strong>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '6px' }}>
                        <button
                          className="um-btn um-btn-small"
                          onClick={() => { setPasswordUser(userDetails.user); setNewPassword(''); }}
                        >
                          Set Password
                        </button>
                        <button
                          className="um-btn um-btn-danger um-btn-small"
                          onClick={() => deleteUser(userDetails.user)}
                        >
                          Delete User
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* KYC Application Summary */}
                {userDetails.kycApplication && (
                  <div style={{ marginBottom: '30px', padding: '15px', backgroundColor: '#2a3b52', borderRadius: '6px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <h3 style={{ color: '#b0b8c4', margin: 0 }}>KYC Application</h3>
                      <button
                        onClick={() => fetchKycDetails(selectedUser)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#ffa726',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        View Full KYC Details
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', color: '#b0b8c4' }}>
                      <div>
                        <strong>Document Type:</strong> {userDetails.kycApplication.document_type}
                      </div>
                      <div>
                        <strong>Document Number:</strong> {userDetails.kycApplication.document_number}
                      </div>
                      <div>
                        <strong>Submitted:</strong> {new Date(userDetails.kycApplication.submitted_at).toLocaleString()}
                      </div>
                      <div>
                        <strong>Status:</strong> 
                        <span style={{
                          color: userDetails.kycApplication.status === 'approved' ? '#00c853' : 
                                 userDetails.kycApplication.status === 'pending' ? '#ffa726' : '#ff1744',
                          fontWeight: 'bold',
                          marginLeft: '5px'
                        }}>
                          {userDetails.kycApplication.status?.toUpperCase() || 'PENDING'}
                        </span>
                      </div>
                      {userDetails.kycApplication.reviewed_at && (
                        <div>
                          <strong>Reviewed:</strong> {new Date(userDetails.kycApplication.reviewed_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Wallet Balances */}
                <div style={{ marginBottom: '30px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3 style={{ color: '#b0b8c4', margin: 0 }}>Wallet Balances</h3>
                    <small style={{ color: '#b0b8c4' }}>Click on balances to edit</small>
                  </div>
                  {userDetails.wallets.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                      {userDetails.wallets.map((wallet) => (
                        <div key={wallet.currency} style={{
                          backgroundColor: '#2a3b52',
                          padding: '15px',
                          borderRadius: '6px',
                          border: '1px solid #344258'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <div style={{ fontWeight: 'bold', color: '#ffffff', fontSize: '16px' }}>
                              {wallet.currency}
                            </div>
                            {!walletEditMode[wallet.currency] ? (
                              <button
                                onClick={() => handleWalletEdit(wallet.currency)}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: '#2962ff',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                Edit
                              </button>
                            ) : (
                              <div style={{ display: 'flex', gap: '5px' }}>
                                <button
                                  onClick={() => updateWalletBalance(selectedUser, wallet.currency, walletUpdates[wallet.currency])}
                                  style={{
                                    padding: '4px 8px',
                                    backgroundColor: '#00c853',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                  }}
                                >
                                  Save
                                </button>
                                <button
                                  onClick={() => cancelWalletEdit(wallet.currency)}
                                  style={{
                                    padding: '4px 8px',
                                    backgroundColor: '#ff1744',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            )}
                          </div>
                          
                          {!walletEditMode[wallet.currency] ? (
                            <>
                              <div style={{ color: '#b0b8c4', fontSize: '14px', marginBottom: '5px' }}>
                                Balance: <span style={{ color: '#ffffff' }}>{parseFloat(wallet.balance).toFixed(8)}</span>
                              </div>
                              <div style={{ color: '#b0b8c4', fontSize: '14px' }}>
                                Locked: <span style={{ color: '#ffffff' }}>{parseFloat(wallet.locked || 0).toFixed(8)}</span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div style={{ marginBottom: '8px' }}>
                                <label style={{ color: '#b0b8c4', fontSize: '12px', display: 'block', marginBottom: '2px' }}>
                                  Balance:
                                </label>
                                <input
                                  type="number"
                                  step="0.00000001"
                                  value={walletUpdates[wallet.currency]?.balance || wallet.balance}
                                  onChange={(e) => handleWalletChange(wallet.currency, 'balance', e.target.value)}
                                  style={{
                                    width: '100%',
                                    padding: '4px 8px',
                                    border: '1px solid #344258',
                                    borderRadius: '4px',
                                    backgroundColor: '#1a2332',
                                    color: '#ffffff',
                                    fontSize: '12px'
                                  }}
                                />
                              </div>
                              <div>
                                <label style={{ color: '#b0b8c4', fontSize: '12px', display: 'block', marginBottom: '2px' }}>
                                  Locked:
                                </label>
                                <input
                                  type="number"
                                  step="0.00000001"
                                  value={walletUpdates[wallet.currency]?.locked || wallet.locked || 0}
                                  onChange={(e) => handleWalletChange(wallet.currency, 'locked', e.target.value)}
                                  style={{
                                    width: '100%',
                                    padding: '4px 8px',
                                    border: '1px solid #344258',
                                    borderRadius: '4px',
                                    backgroundColor: '#1a2332',
                                    color: '#ffffff',
                                    fontSize: '12px'
                                  }}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: '#b0b8c4', textAlign: 'center', padding: '20px' }}>
                      No wallet balances found
                    </div>
                  )}
                </div>

                {/* Recent Orders */}
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ color: '#b0b8c4', marginBottom: '15px' }}>Recent Orders</h3>
                  {userDetails.recentOrders.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #344258' }}>
                            <th style={{ padding: '8px', textAlign: 'left', color: '#b0b8c4' }}>Pair</th>
                            <th style={{ padding: '8px', textAlign: 'left', color: '#b0b8c4' }}>Type</th>
                            <th style={{ padding: '8px', textAlign: 'left', color: '#b0b8c4' }}>Price</th>
                            <th style={{ padding: '8px', textAlign: 'left', color: '#b0b8c4' }}>Amount</th>
                            <th style={{ padding: '8px', textAlign: 'left', color: '#b0b8c4' }}>Status</th>
                            <th style={{ padding: '8px', textAlign: 'left', color: '#b0b8c4' }}>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userDetails.recentOrders.map((order) => (
                            <tr key={order.id} style={{ borderBottom: '1px solid #2a3b52' }}>
                              <td style={{ padding: '8px' }}>{order.pair}</td>
                              <td style={{ 
                                padding: '8px', 
                                color: order.type === 'buy' ? '#00c853' : '#ff1744',
                                fontWeight: 'bold'
                              }}>
                                {order.type.toUpperCase()}
                              </td>
                              <td style={{ padding: '8px' }}>${order.price}</td>
                              <td style={{ padding: '8px' }}>{order.amount}</td>
                              <td style={{ padding: '8px' }}>
                                <span style={{
                                  color: order.status === 'filled' ? '#00c853' : 
                                         order.status === 'open' ? '#2962ff' : '#ff1744'
                                }}>
                                  {order.status}
                                </span>
                              </td>
                              <td style={{ padding: '8px' }}>
                                {new Date(order.created_at).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ color: '#b0b8c4', textAlign: 'center', padding: '20px' }}>
                      No recent orders found
                    </div>
                  )}
                </div>

                {/* Recent Trades */}
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ color: '#b0b8c4', marginBottom: '15px' }}>Recent Trades</h3>
                  {userDetails.recentTrades.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #344258' }}>
                            <th style={{ padding: '8px', textAlign: 'left', color: '#b0b8c4' }}>Pair</th>
                            <th style={{ padding: '8px', textAlign: 'left', color: '#b0b8c4' }}>Type</th>
                            <th style={{ padding: '8px', textAlign: 'left', color: '#b0b8c4' }}>Price</th>
                            <th style={{ padding: '8px', textAlign: 'left', color: '#b0b8c4' }}>Amount</th>
                            <th style={{ padding: '8px', textAlign: 'left', color: '#b0b8c4' }}>Total</th>
                            <th style={{ padding: '8px', textAlign: 'left', color: '#b0b8c4' }}>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userDetails.recentTrades.map((trade) => (
                            <tr key={trade.id} style={{ borderBottom: '1px solid #2a3b52' }}>
                              <td style={{ padding: '8px' }}>{trade.pair}</td>
                              <td style={{ 
                                padding: '8px', 
                                color: trade.type === 'buy' ? '#00c853' : '#ff1744',
                                fontWeight: 'bold'
                              }}>
                                {trade.type.toUpperCase()}
                              </td>
                              <td style={{ padding: '8px' }}>${trade.price}</td>
                              <td style={{ padding: '8px' }}>{trade.amount}</td>
                              <td style={{ padding: '8px' }}>${trade.total}</td>
                              <td style={{ padding: '8px' }}>
                                {new Date(trade.created_at).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ color: '#b0b8c4', textAlign: 'center', padding: '20px' }}>
                      No recent trades found
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ color: '#ff1744', textAlign: 'center' }}>
                Failed to load user details
              </div>
            )}
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="um-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="um-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Create User</h3>
            <form onSubmit={createUser} className="um-form">
              <label>Name</label>
              <input
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                required
              />
              <label>Email</label>
              <input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                required
              />
              <label>Password</label>
              <input
                type="password"
                minLength={6}
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                required
              />
              <label>Role</label>
              <select
                value={createForm.role}
                onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
              >
                <option value="user">User</option>
                <option value="moderator">Moderator</option>
                <option value="admin">Admin</option>
              </select>
              <div className="um-form-actions">
                <button type="button" className="um-btn" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" className="um-btn um-btn-primary" disabled={savingPassword}>
                  {savingPassword ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {passwordUser && (
        <div className="um-modal-overlay" onClick={() => setPasswordUser(null)}>
          <div className="um-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Set Password</h3>
            <p className="um-modal-copy">
              Update password for <strong>{passwordUser.email}</strong>. All of this user's other logins will be signed out.
            </p>
            <form onSubmit={updateUserPassword} className="um-form">
              <label>New password</label>
              <input
                type="password"
                minLength={6}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder="At least 6 characters"
              />
              <div className="um-form-actions">
                <button type="button" className="um-btn" onClick={() => setPasswordUser(null)}>Cancel</button>
                <button type="submit" className="um-btn um-btn-primary" disabled={savingPassword}>
                  {savingPassword ? 'Saving...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* KYC Details Modal */}
      {showKycModal && kycDetails && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: '#1a2332',
            border: '1px solid #344258',
            borderRadius: '8px',
            padding: '20px',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2>KYC Verification Details</h2>
              <button
                onClick={closeKycModal}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ff1744',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>

            {/* KYC Information */}
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ color: '#b0b8c4', marginBottom: '15px' }}>Application Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                <div>
                  <strong style={{ color: '#b0b8c4' }}>User:</strong>
                  <div style={{ color: '#ffffff' }}>{kycDetails.user_name} ({kycDetails.user_email})</div>
                </div>
                <div>
                  <strong style={{ color: '#b0b8c4' }}>Document Type:</strong>
                  <div style={{ color: '#ffffff', textTransform: 'capitalize' }}>
                    {kycDetails.document_type?.replace('_', ' ') || 'N/A'}
                  </div>
                </div>
                <div>
                  <strong style={{ color: '#b0b8c4' }}>Document Number:</strong>
                  <div style={{ color: '#ffffff', fontFamily: 'monospace' }}>
                    {kycDetails.document_number || 'N/A'}
                  </div>
                </div>
                <div>
                  <strong style={{ color: '#b0b8c4' }}>Status:</strong>
                  <div style={{
                    color: kycDetails.status === 'approved' ? '#00c853' : 
                           kycDetails.status === 'pending' ? '#ffa726' : '#ff1744',
                    fontWeight: 'bold'
                  }}>
                    {kycDetails.status?.toUpperCase() || 'PENDING'}
                  </div>
                </div>
                <div>
                  <strong style={{ color: '#b0b8c4' }}>Submitted:</strong>
                  <div style={{ color: '#ffffff' }}>
                    {new Date(kycDetails.submitted_at).toLocaleString()}
                  </div>
                </div>
                {kycDetails.reviewed_at && (
                  <div>
                    <strong style={{ color: '#b0b8c4' }}>Reviewed:</strong>
                    <div style={{ color: '#ffffff' }}>
                      {new Date(kycDetails.reviewed_at).toLocaleString()}
                    </div>
                  </div>
                )}
                {kycDetails.review_notes && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <strong style={{ color: '#b0b8c4' }}>Review Notes:</strong>
                    <div style={{ 
                      color: '#ffffff', 
                      backgroundColor: '#2a3b52',
                      padding: '10px',
                      borderRadius: '4px',
                      marginTop: '5px'
                    }}>
                      {kycDetails.review_notes}
                    </div>
                  </div>
                )}
              </div>

              {/* KYC Status Update */}
              
            </div>

            {/* KYC Images */}
            <div>
              <h3 style={{ color: '#b0b8c4', marginBottom: '15px' }}>Document Images</h3>
              
              <KYCImage 
                src={kycDetails.document_front_url} 
                alt="Document Front" 
                title="Document Front"
              />
              
              <KYCImage 
                src={kycDetails.document_back_url} 
                alt="Document Back" 
                title="Document Back"
              />
              
              <KYCImage 
                src={kycDetails.selfie_url} 
                alt="Selfie with Document" 
                title="Selfie with Document"
              />
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination" style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '10px' }}>
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            style={{
              padding: '8px 16px',
              border: '1px solid #344258',
              backgroundColor: currentPage === 1 ? '#2a3b52' : '#2962ff',
              color: '#ffffff',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              borderRadius: '4px'
            }}
          >
            Previous
          </button>
          
          <span style={{ color: '#b0b8c4', padding: '8px 16px', alignSelf: 'center' }}>
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 16px',
              border: '1px solid #344258',
              backgroundColor: currentPage === totalPages ? '#2a3b52' : '#2962ff',
              color: '#ffffff',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              borderRadius: '4px'
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default UserManagement;